/**
 * LgpdRequestStatusBadge — badge colorido para status de LGPDRequest.
 *
 * 9 status canônicos ADR-098 (valores EN do backend, labels PT):
 *   received | identity_validation | identity_validated | identity_rejected |
 *   in_processing | pending_information | completed | rejected_by_law |
 *   cancelled_by_requester
 *
 * P15: badge visual apenas — sem dados do titular expostos.
 */

import type { LgpdRequestStatus } from './types';

const STATUS_CONFIG: Record<LgpdRequestStatus, { label: string; bg: string; color: string }> = {
  received: { label: 'Recebida', bg: '#eff6ff', color: '#1d4ed8' },
  identity_validation: { label: 'Validando identidade', bg: '#fef9c3', color: '#a16207' },
  identity_validated: { label: 'Identidade validada', bg: '#f0fdf4', color: '#166534' },
  identity_rejected: { label: 'Identidade rejeitada', bg: '#fef2f2', color: '#b91c1c' },
  in_processing: { label: 'Em processamento', bg: '#fef3c7', color: '#92400e' },
  pending_information: { label: 'Pendente de informação', bg: '#fdf4ff', color: '#7e22ce' },
  completed: { label: 'Concluída', bg: '#f0fdf4', color: '#15803d' },
  rejected_by_law: { label: 'Rejeitada (base legal)', bg: '#fef2f2', color: '#b91c1c' },
  cancelled_by_requester: { label: 'Cancelada pelo titular', bg: '#f9fafb', color: '#6b7280' },
};

interface LgpdRequestStatusBadgeProps {
  status: LgpdRequestStatus;
  size?: 'sm' | 'md';
}

export function LgpdRequestStatusBadge({
  status,
  size = 'sm',
}: LgpdRequestStatusBadgeProps): JSX.Element {
  const config = STATUS_CONFIG[status] ?? { label: status, bg: '#f9fafb', color: '#6b7280' };
  const fontSize = size === 'md' ? 12 : 10;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: size === 'md' ? '3px 8px' : '2px 6px',
        borderRadius: 4,
        fontSize,
        fontWeight: 600,
        fontFamily: 'var(--font-mono, monospace)',
        background: config.bg,
        color: config.color,
        whiteSpace: 'nowrap',
      }}
      data-testid="status-badge"
    >
      {config.label}
    </span>
  );
}

// Valores EN do backend → label PT (art. LGPD). `anonymization` cobre o antigo
// FE-only `eliminacao` (Art. 18 VI). `restricao` não existe no backend (removido).
export const REQUEST_TYPE_LABEL: Record<string, string> = {
  access: 'Acesso (art. 18, II)',
  correction: 'Correção (art. 18, III)',
  anonymization: 'Eliminação/Anonimização (art. 18, VI) ⚠',
  portability: 'Portabilidade (art. 18, V)',
  opposition: 'Oposição (art. 18, §2º)',
  consent_withdrawal: 'Revogação de consentimento',
};
